// THE SIGN-OFF ISOMETRIC, as data (owner 2026-11-05: "the same generated isometric
// graphics, split by level, area and system, like the project at a glance, instead of the
// 3D and tiles").
//
// The stage is drawn from TWO sources, in the SAME coordinate space - feet in the model's
// own plan coordinates:
//
//   the tile set   every level's and every area's footprint, from the model export the
//                  sign-off is recorded against (tile.i -> plan x, tile.j -> plan y,
//                  tile.k -> height). Always there, so the stage always draws.
//   the glance     the fine geometry, when the project has a published manifest: one path
//                  per `level|system|zone` bucket, the same ones Project at a Glance draws.
//
// SPLIT BY LEVEL, AREA AND SYSTEM is therefore exact on the first two (they are what the
// sign-off targets are) and as good as the drawings on the third.
//
// Systems come in two vocabularies and the picker shows both, each labelled by where it
// comes from (owner 2026-11-05):
//
//   DRAWN LAYERS    the discipline layers of the drawings - Mechanical, Power, Lighting,
//                   Security, Telecom, Plumbing, Underground, Roof, Process piping, Fire
//                   protection. These are what the isometric draws.
//   IN THE MODEL    the systems the export actually carries, per area, with their element
//                   counts - Conduits, Cable tray, Busway, Gear ... These are what element
//                   coverage is counted from.
//
// A sign-off on a drawn-only layer covers no model elements; it is still recorded, and the
// request says so (utils/signoffRequests.coverNote).

// Each drawn layer, with the glance system id it is drawn from and its colour token.
// `label` is the owner's word for it; `glance` is the id the manifest uses.
export const DRAWN_LAYERS = [
  { id: "ducts", label: "Mechanical", token: "--duct", discipline: "mechanical" },
  { id: "power", label: "Power", token: "--pwr", discipline: "electrical" },
  { id: "lighting", label: "Lighting", token: "--ltg", discipline: "electrical" },
  { id: "security", label: "Security", token: "--sec", discipline: "security" },
  { id: "telecom", label: "Telecom", token: "--tel", discipline: "telecom" },
  { id: "plumbing", label: "Plumbing", token: "--pipe", discipline: "plumbing" },
  { id: "underfloor", label: "Underground", token: "--under", discipline: "plumbing" },
  { id: "processPiping", label: "Process piping", token: "--proc", discipline: "mechanical" },
  { id: "sprinklers", label: "Fire protection", token: "--fire", discipline: "fire" },
  { id: "roof", label: "Roof", token: "--rtutop", discipline: "mechanical" },
];
export const drawnLayer = (id) => DRAWN_LAYERS.find((l) => l.id === id) || null;
/** A drawn layer is picked as "layer:<id>" so it never collides with a model system's name. */
export const LAYER_PREFIX = "layer:";
export const isLayer = (s) => String(s || "").startsWith(LAYER_PREFIX);
export const layerIdOf = (s) => String(s || "").slice(LAYER_PREFIX.length);
export const layerValue = (id) => `${LAYER_PREFIX}${id}`;

/** A glance level id ("L1", "roof") as a comparable key. Ids are already short codes. */
export const normKey = (s) => String(s || "").trim().toUpperCase().replace(/[^A-Z0-9]/g, "");

const ORDINALS = ["first", "second", "third", "fourth", "fifth", "sixth", "seventh", "eighth", "ninth", "tenth"];

/**
 * THE LEVEL KEY A LEVEL NAME BELONGS TO - back/utils/elementAreas.levelKey, written again
 * here because the browser has to agree with the server about which floor a name means.
 * BA03's schedule writes the same floor "Level 1", "L1" and "Level1", and its areas write
 * "Level 1" while the drawings say "L1"; all of them are L1. Stripping the punctuation is
 * NOT enough - that gives LEVEL1, which matches nothing the server ever stored.
 */
export function levelKeyOf(raw) {
  const v = String(raw || "").trim().toLowerCase();
  if (!v) return "";
  let m = /^(?:level|lvl|l|floor|fl)?[\s._-]*0*(\d{1,3})$/.exec(v);
  if (m) return `L${Number(m[1])}`;
  m = /^(first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth)\s+(?:floor|level|storey|story)$/.exec(v);
  if (m) return `L${ORDINALS.indexOf(m[1]) + 1}`;
  return v.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

/**
 * THE BUILDING'S STRUCTURE, from the PARSED DOCUMENTS first (owner 2026-11-05: "we don't
 * see the same parsing of all documents to create the same structure as the technical
 * documentation, project at a glance ... we need all levels and all areas and their
 * systems").
 *
 * The first cut of this page read the structure off the TILE SET, which is the model
 * export after placement - so it could only ever show what the placement reached. On BA03
 * that was one level of four and six areas of twenty-nine: the other three levels, the
 * twenty-three unplaced areas, the rooms, the zones and the grid were all in the glance
 * manifest and none of it was drawn. The structure is now the UNION of three sources, and
 * each level, area and system says which one it came from:
 *
 *   the drawings   the glance manifest - levels with their rooms, footprints and order,
 *                  the grid zones, and the systems that are actually drawn, per level
 *   the areas      every current coordination area, on the level it belongs to, whether or
 *                  not a single model element has been placed in it
 *   the model      the tile set: which levels and areas the export has elements in, and
 *                  how many
 *
 * Nothing is invented: a level with no footprint in the drawings is drawn on the project's
 * outline and says so, and an area nothing locates is listed rather than placed.
 */
export function structureFrom({ tiles = [], areas = {}, levelNames = {}, manifest = null } = {}) {
  const rows = Array.isArray(tiles) ? tiles : (tiles && tiles.tiles) || [];
  const lv = new Map();
  const get = (key) => {
    if (!key) return null;
    if (!lv.has(key)) {
      lv.set(key, {
        key, name: "", order: null, meanK: null, rooms: [], footprint: [], glanceId: "",
        fromDocs: false, fromAreas: false, fromModel: false, elements: 0, tiles: 0,
      });
    }
    return lv.get(key);
  };

  // 1. the drawings
  for (const l of (manifest && manifest.levels) || []) {
    const row = get(levelKeyOf(l.id));
    if (!row) continue;
    row.fromDocs = true;
    row.glanceId = l.id;
    row.rooms = l.rooms || [];
    row.footprint = l.footprint || [];
    if (l.order != null) row.order = l.order;
    if (l.name) row.name = l.name;
  }
  // 2. the model
  const sum = new Map();
  for (const t of rows) {
    for (const g of t.g || []) {
      if (!g.l) continue;
      const a = sum.get(g.l) || { k: 0, n: 0, cells: new Set() };
      a.k += (t.k || 0) * (g.n || 1);
      a.n += g.n || 1;
      a.cells.add(`${t.i},${t.j}`);
      sum.set(g.l, a);
    }
  }
  for (const [key, a] of sum) {
    const row = get(key);
    row.fromModel = true;
    row.meanK = a.n ? a.k / a.n : 0;
    row.elements = a.n;
    row.tiles = a.cells.size;
  }
  // 3. the areas - a level nothing is placed on and no drawing covers is still a level
  const areaRows = Object.entries(areas || {})
    .filter(([, a]) => a && a.current !== false)
    .map(([id, a]) => ({ id, name: a.name || "Area", level: a.level || "", levelKey: a.levelKey || "", systems: a.systems || [] }));
  for (const a of areaRows) if (a.levelKey) get(a.levelKey).fromAreas = true;

  for (const row of lv.values()) if (!row.name) row.name = levelNames[row.key] || row.key;

  // bottom first: the drawings' own order, else the height of the model's tiles, else the
  // number in the key; a level with no number at all (ROOF) goes on top
  const numOf = (k) => { const m = /(\d+)/.exec(k || ""); return m ? Number(m[1]) : null; };
  const levels = [...lv.values()].sort((x, y) => {
    const px = x.order != null ? x.order : x.meanK != null ? x.meanK : numOf(x.key);
    const py = y.order != null ? y.order : y.meanK != null ? y.meanK : numOf(y.key);
    if (px == null && py != null) return 1;
    if (py == null && px != null) return -1;
    if (px != null && py != null && px !== py) return px - py;
    const nx = numOf(x.key); const ny = numOf(y.key);
    if (nx != null && ny != null && nx !== ny) return nx - ny;
    return String(x.name).localeCompare(String(y.name), undefined, { numeric: true });
  }).map((l, i) => ({ ...l, rank: i + 1 }));

  // the areas of each level, and the ones that belong to no level (they usually span the
  // building - BA03's A to F are the grid zones' own names)
  const byLevel = new Map(levels.map((l) => [l.key, []]));
  const unlevelled = [];
  for (const a of areaRows) {
    const bucket = a.levelKey && byLevel.has(a.levelKey) ? byLevel.get(a.levelKey) : null;
    if (bucket) bucket.push(a); else unlevelled.push(a);
  }
  return { levels, areasByLevel: byLevel, unlevelled, areas: areaRows, layers: layersFrom(manifest) };
}

/**
 * The systems the DRAWINGS carry, from the manifest itself - its own labels, colours and
 * height bands - and which levels each is drawn on (its buckets). Without a manifest the
 * owner's standing list is offered instead, each marked as not drawn.
 */
export function layersFrom(manifest) {
  const buckets = (manifest && manifest.buckets) || {};
  const onLevel = new Map();
  for (const k of Object.keys(buckets)) {
    const [lvl, sys] = String(k).split("|");
    if (!lvl || !sys) continue;
    if (!onLevel.has(sys)) onLevel.set(sys, new Set());
    onLevel.get(sys).add(levelKeyOf(lvl));
  }
  const drawn = ((manifest && manifest.systems) || []).filter((s) => s.tier != null);
  if (!drawn.length) {
    return DRAWN_LAYERS.map((l) => ({
      id: l.id, label: l.label, token: l.token, tier: null, draw: "", levels: [], drawn: false,
    }));
  }
  return drawn.map((s) => ({
    id: s.id,
    label: (DRAWN_LAYERS.find((l) => l.id === s.id) || {}).label || s.label || s.id,
    glanceLabel: s.label || "",
    token: String(s.token || "").startsWith("--") ? s.token : `--${s.token || "system"}`,
    tier: s.tier, draw: s.draw || "", coverage: s.coverage || null,
    levels: [...(onLevel.get(s.id) || [])],
    drawn: (onLevel.get(s.id) || new Set()).size > 0,
  })).sort((a, b) => (a.tier || 0) - (b.tier || 0));
}

/**
 * The levels of the stage, bottom first, from the tile set itself: a level's height is the
 * mean `k` of the tiles that hold its elements, so the stack is in the building's own order
 * without anybody numbering the levels. `names`: the timeline's levelKey -> name.
 *
 * Kept for the places that only have the tile set (the emailed link page's drawing); the
 * page itself uses structureFrom, which also knows the levels the DRAWINGS carry.
 */
export function levelsFrom(tiles, names = {}) {
  const sum = new Map();
  for (const t of tiles || []) {
    for (const g of t.g || []) {
      if (!g.l) continue;
      const a = sum.get(g.l) || { k: 0, n: 0, cells: 0 };
      a.k += (t.k || 0) * (g.n || 1);
      a.n += g.n || 1;
      a.cells += 1;
      sum.set(g.l, a);
    }
  }
  return [...sum.entries()]
    .map(([key, a]) => ({ key, name: names[key] || key, height: a.n ? a.k / a.n : 0, tiles: a.cells, elements: a.n }))
    .sort((x, y) => x.height - y.height || String(x.name).localeCompare(String(y.name), undefined, { numeric: true }))
    .map((l, i) => ({ ...l, rank: i + 1 }));
}

/** Every area of a level, with how many of its elements sit on that level. */
export function areasOnLevel(tiles, levelKey) {
  const n = new Map();
  for (const t of tiles || []) {
    for (const g of t.g || []) {
      if (g.l !== levelKey) continue;
      for (const a of [g.a, ...(g.aa || [])]) if (a) n.set(a, (n.get(a) || 0) + (g.n || 0));
    }
  }
  return n;
}

/**
 * The plan cells of one level, as "i,j" -> elements, for the tiles that match.
 * `pick(group)` says whether a group counts; omitted, every placed group on the level does.
 */
export function cellsOn(tiles, levelKey, pick = null) {
  const cells = new Map();
  // no level means no plate: an element the placement could not put on a level is on none
  // of them, and must never be drawn as though it were (isoModel.test.js)
  if (!levelKey) return cells;
  for (const t of tiles || []) {
    let n = 0;
    for (const g of t.g || []) {
      if (g.l !== levelKey) continue;
      if (pick && !pick(g)) continue;
      n += g.n || 0;
    }
    if (n) {
      const k = `${t.i},${t.j}`;
      cells.set(k, (cells.get(k) || 0) + n);
    }
  }
  return cells;
}

export const inArea = (areaId) => (g) => g.a === areaId || (g.aa || []).includes(areaId);
export const hasSystem = (system) => {
  const want = String(system || "").toLowerCase();
  return (g) => (g.s || []).some((x) => String(x).toLowerCase() === want);
};

/** The plan extent, in model units, of some "i,j" cells. */
export function cellBounds(cellKeys, grid) {
  const e = (grid && grid.edge) || 1;
  const ox = (grid && grid.origin && grid.origin.x) || 0;
  const oy = (grid && grid.origin && grid.origin.z) || 0;
  let xmin = Infinity; let ymin = Infinity; let xmax = -Infinity; let ymax = -Infinity;
  for (const k of cellKeys) {
    const [i, j] = String(k).split(",").map(Number);
    xmin = Math.min(xmin, ox + i * e); xmax = Math.max(xmax, ox + (i + 1) * e);
    ymin = Math.min(ymin, oy + j * e); ymax = Math.max(ymax, oy + (j + 1) * e);
  }
  if (!Number.isFinite(xmin)) return { xmin: 0, ymin: 0, xmax: 1, ymax: 1, w: 1, h: 1 };
  return { xmin, ymin, xmax, ymax, w: Math.max(1, xmax - xmin), h: Math.max(1, ymax - ymin) };
}

/**
 * One SVG path for some "i,j" cells, each cell the projected square of the grid. `project`
 * is the stack projector's (x, y, rank) - the same one the glance buckets are drawn with,
 * so a tile footprint and a drawn pipe land on the same plate.
 */
export function cellsPath(cellKeys, grid, project, rank) {
  const e = (grid && grid.edge) || 1;
  const ox = (grid && grid.origin && grid.origin.x) || 0;
  const oy = (grid && grid.origin && grid.origin.z) || 0;
  const f = (v) => v.toFixed(1);
  let d = "";
  for (const k of cellKeys) {
    const [i, j] = String(k).split(",").map(Number);
    const x0 = ox + i * e; const y0 = oy + j * e;
    const c = [[x0, y0], [x0 + e, y0], [x0 + e, y0 + e], [x0, y0 + e]].map(([x, y]) => project(x, y, rank));
    d += `M${f(c[0][0])} ${f(c[0][1])}L${f(c[1][0])} ${f(c[1][1])}L${f(c[2][0])} ${f(c[2][1])}L${f(c[3][0])} ${f(c[3][1])}Z`;
  }
  return d;
}

/** The centre of some cells on screen, for a label. */
export function cellsCentre(cellKeys, grid, project, rank) {
  const b = cellBounds(cellKeys, grid);
  return project((b.xmin + b.xmax) / 2, (b.ymin + b.ymax) / 2, rank);
}

/**
 * THE SCOPE AS SIGN-OFF TARGETS (owner 2026-11-05: "a user can select 1 or multiple
 * systems, per 1 or multiple areas in 1 or multiple levels").
 *
 * The cross product, collapsed the way the sign-off already reads:
 *   no area picked on a level            -> one `level` target (everything on it)
 *   areas picked, no system              -> one `area` target each
 *   areas and systems picked             -> one `system` target per area x system
 * A system on its own, with no area, cannot be a target: a system sign-off is always a
 * system IN an area (targetKey `system|<areaSetId>|<system>`), which is what it was before
 * the 3D volumes and is what it is again.
 *
 * `levels` HERE IS THE TIMELINE'S OWN MAP, not the names the drawing shows. The server
 * works a level target's key back out of its NAME (utils/elementAreas.levelKey), and the
 * timeline's names are the ones its keys were made from, so they round-trip. A name from
 * somewhere else - the glance manifest calls L2 "Second floor" - does not: it would come
 * back as SECONDFLOOR and the target would cover no tiles. The key is sent as the name
 * when the timeline has none.
 */
export function scopeTargets({ levelKeys = [], areaIds = [], systems = [] }, { areas = {}, levels = {} } = {}) {
  const out = [];
  const seen = new Set();
  const push = (t, k) => { if (!seen.has(k)) { seen.add(k); out.push(t); } };
  const picked = new Set(areaIds);
  const levelsOf = (id) => (areas[id] && areas[id].levelKey) || "";
  // areas picked are grouped under the levels they belong to; a level with no area picked
  // is signed off whole
  const areasByLevel = new Map();
  for (const id of picked) {
    const lk = levelsOf(id);
    if (levelKeys.length && !levelKeys.includes(lk)) continue;
    if (!areasByLevel.has(lk)) areasByLevel.set(lk, []);
    areasByLevel.get(lk).push(id);
  }
  const keys = levelKeys.length ? levelKeys : [...areasByLevel.keys()];
  for (const lk of keys) {
    const mine = areasByLevel.get(lk) || [];
    if (!mine.length) {
      if (!lk) continue;
      push({ kind: "level", levelKey: lk, level: levels[lk] || lk }, `level|${lk}`);
      continue;
    }
    for (const id of mine) {
      if (!systems.length) { push({ kind: "area", areaSetId: id }, `area|${id}`); continue; }
      for (const s of systems) {
        const name = isLayer(s) ? (drawnLayer(layerIdOf(s)) || {}).label || layerIdOf(s) : s;
        push({ kind: "system", areaSetId: id, system: name }, `system|${id}|${String(name).toLowerCase()}`);
      }
    }
  }
  return out;
}

/**
 * The systems to offer for the areas picked, in the two groups the owner asked for.
 * `manifest`: the glance manifest, when the project has one - its drawn systems are the
 * layers that actually have geometry on this project; without one every layer is offered
 * and says it is not drawn yet.
 */
export function systemOptions({ areaIds = [], areas = {}, tiles = [], manifest = null } = {}) {
  const rows = Array.isArray(tiles) ? tiles : (tiles && tiles.tiles) || [];
  const drawnIds = new Set(((manifest && manifest.systems) || []).filter((s) => s.tier != null).map((s) => s.id));
  const layers = DRAWN_LAYERS.map((l) => ({
    value: layerValue(l.id), label: l.label, group: "drawn", token: l.token,
    drawn: drawnIds.has(l.id), note: drawnIds.has(l.id) ? "drawn" : "not in the drawings yet",
  }));
  // the model's own systems, counted over the areas picked (every area when none is)
  const want = new Set(areaIds);
  const counts = new Map();
  for (const t of rows) {
    for (const g of t.g || []) {
      const mine = [g.a, ...(g.aa || [])].filter(Boolean);
      if (want.size && !mine.some((a) => want.has(a))) continue;
      for (const s of g.s || []) counts.set(s, (counts.get(s) || 0) + (g.n || 0));
    }
  }
  // a system a picked area declares but the model does not carry is still offered
  for (const id of want) for (const s of ((areas[id] || {}).systems) || []) if (!counts.has(s)) counts.set(s, 0);
  const model = [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([name, n]) => ({ value: name, label: name, group: "model", elements: n, note: n ? `${n.toLocaleString()} elements` : "none in this export" }));
  return { layers, model };
}

/**
 * WHERE AN AREA SITS ON A PLATE. Three sources, best first, and the caller is told which
 * one it got - an area nothing locates is LISTED, never drawn in a made-up place.
 *
 *   'model'  the cells of the tiles that hold its elements on that level
 *   'zone'   the grid zone whose label names it. BA03's zones are called "Areas C and F
 *            (grids 9 to 27)", so the drawings do say where area C is even where no
 *            element has been placed in it; the band spans the plate between its grids.
 *   ''       nothing locates it
 */
export function areaPlacement(area, levelKey, { tiles = [], manifest = null } = {}) {
  const rows = Array.isArray(tiles) ? tiles : (tiles && tiles.tiles) || [];
  const cells = cellsOn(rows, levelKey, inArea(area.id));
  if (cells.size) return { kind: "model", cells, zones: [] };
  const zones = zonesNaming(area.name, manifest);
  if (zones.length) return { kind: "zone", cells: new Set(), zones };
  return { kind: "", cells: new Set(), zones: [] };
}

/**
 * The grid zones whose label names this area. The label is read as words, so "Areas C and
 * F" names C and F, and "Area A" names A - but "grids 12 to 10" names nothing, and an area
 * called "Complete" is not matched by the word "complete" appearing in some other label.
 */
export function zonesNaming(areaName, manifest) {
  const name = String(areaName || "").trim();
  if (!name) return [];
  // the area's own short name: "Area C" -> "C", "L1 10-Line" -> "L1 10-LINE"
  const short = (/^area\s+(.+)$/i.exec(name) || [])[1] || name;
  const want = short.trim().toUpperCase();
  if (!want) return [];
  return ((manifest && manifest.zones) || []).filter((z) => {
    const label = String(z.label || "");
    if (!/\bareas?\b/i.test(label)) return false; // "grids 12 to 10" names no area
    // the names a label lists: "Areas C and F (grids 9 to 27)" -> C, F
    const head = label.replace(/\(.*$/, "");
    const parts = head.replace(/^\s*areas?\s+/i, "").split(/\s*(?:,|and|&|\+)\s*/i);
    return parts.map((x) => x.trim().toUpperCase()).filter(Boolean).includes(want);
  });
}

/** A zone's band on a plate, as a polygon in plan feet, across the level's own extent. */
export function zoneBand(zone, bounds) {
  const x0 = Math.max(zone.x0, bounds.xmin);
  const x1 = Math.min(zone.x1, bounds.xmax);
  if (!(x1 > x0)) return [];
  const y0 = zone.y0 != null ? Math.max(zone.y0, bounds.ymin) : bounds.ymin;
  const y1 = zone.y1 != null ? Math.min(zone.y1, bounds.ymax) : bounds.ymax;
  return [[x0, y0], [x1, y0], [x1, y1], [x0, y1]];
}

/** A polygon as an SVG path, projected onto a plate. */
export function polyPath(poly, project, rank) {
  if (!poly || poly.length < 3) return "";
  let d = "";
  poly.forEach((p, i) => {
    const q = project(p[0], p[1], rank);
    d += `${i ? "L" : "M"}${q[0].toFixed(1)} ${q[1].toFixed(1)}`;
  });
  return `${d}Z`;
}

/** The plan extent of a polygon list (the levels' footprints), in plan feet. */
export function polyBounds(polys) {
  let xmin = Infinity; let ymin = Infinity; let xmax = -Infinity; let ymax = -Infinity;
  for (const poly of polys || []) {
    for (const p of poly || []) {
      xmin = Math.min(xmin, p[0]); xmax = Math.max(xmax, p[0]);
      ymin = Math.min(ymin, p[1]); ymax = Math.max(ymax, p[1]);
    }
  }
  if (!Number.isFinite(xmin)) return null;
  return { xmin, ymin, xmax, ymax, w: Math.max(1, xmax - xmin), h: Math.max(1, ymax - ymin) };
}

/** Two plan extents merged; either may be null. */
export function mergeBounds(a, b) {
  if (!a) return b;
  if (!b) return a;
  const xmin = Math.min(a.xmin, b.xmin); const xmax = Math.max(a.xmax, b.xmax);
  const ymin = Math.min(a.ymin, b.ymin); const ymax = Math.max(a.ymax, b.ymax);
  return { xmin, ymin, xmax, ymax, w: Math.max(1, xmax - xmin), h: Math.max(1, ymax - ymin) };
}

/**
 * REGISTERING THE TWO FRAMES. The drawings and the model do not always agree on where the
 * building is: on BA03 the manifest's plan y runs 1188..1817 while the tile grid's runs
 * -671..510, nearly 1,900 ft apart, because the manifest is registered to the DRAWINGS'
 * grid and the tile set comes from the export's own scene frame. Drawing one on top of the
 * other without a map puts an area a third of a mile from the room it is in.
 *
 * The map is the grid: both sides name their lines, so matching the names gives a straight
 * line per axis (model = a * drawing + b) by least squares. `ok` is false when fewer than
 * two lines match or the worst residual is over 6 in - the same threshold and the same
 * honesty as utils/glance/zones.fitAxis. A caller with `ok: false` must NOT mix the frames.
 *
 *   lines:     the model-frame grid, [{ label, start: [x, y, z], end }] (api getGridLines)
 *   grid:      the manifest's own, { x: { "12": -165.1 }, y: {...} }
 */
export function registerFrames(lines, grid) {
  const fit = (axis) => {
    const named = (grid && grid[axis]) || {};
    const pts = [];
    for (const [label, drawV] of Object.entries(named)) {
      const line = (lines || []).find((l) => String(l.label) === String(label));
      if (!line || !Array.isArray(line.start) || !Array.isArray(line.end)) continue;
      const s = line.start; const e = line.end;
      // a grid line is constant on the axis it sits on; plan y is the model's z
      const modelV = Math.abs(s[0] - e[0]) < 1e-6 ? s[0] : Math.abs(s[2] - e[2]) < 1e-6 ? s[2] : null;
      if (modelV == null || !Number.isFinite(Number(drawV))) continue;
      pts.push([Number(drawV), modelV]);
    }
    if (pts.length < 2) return { ok: false, a: 1, b: 0, n: pts.length, residualIn: null };
    const n = pts.length;
    const sx = pts.reduce((t, p) => t + p[0], 0);
    const sy = pts.reduce((t, p) => t + p[1], 0);
    const sxx = pts.reduce((t, p) => t + p[0] * p[0], 0);
    const sxy = pts.reduce((t, p) => t + p[0] * p[1], 0);
    const den = n * sxx - sx * sx;
    if (!den) return { ok: false, a: 1, b: 0, n, residualIn: null };
    const a = (n * sxy - sx * sy) / den;
    const b = (sy - a * sx) / n;
    const residual = Math.max(...pts.map((p) => Math.abs(a * p[0] + b - p[1])));
    return { ok: residual * 12 <= 6, a, b, n, residualIn: residual * 12 };
  };
  const x = fit("x");
  const y = fit("y");
  return {
    ok: x.ok && y.ok,
    x,
    y,
    // a model-frame point into the drawings' frame, which is what the plates are drawn in
    toDrawing: (mx, my) => [x.a ? (mx - x.b) / x.a : mx, y.a ? (my - y.b) / y.a : my],
  };
}

/**
 * Some "i,j" cells as polygons in plan coordinates. `map` carries each corner into another
 * frame (registerFrames().toDrawing), so the model's footprints can be drawn on a plate
 * that came from the drawings - but only when the registration said `ok`.
 */
export function cellPolys(cellKeys, grid, map = null) {
  const e = (grid && grid.edge) || 1;
  const ox = (grid && grid.origin && grid.origin.x) || 0;
  const oy = (grid && grid.origin && grid.origin.z) || 0;
  const out = [];
  for (const k of cellKeys) {
    const [i, j] = String(k).split(",").map(Number);
    const x0 = ox + i * e; const y0 = oy + j * e;
    const poly = [[x0, y0], [x0 + e, y0], [x0 + e, y0 + e], [x0, y0 + e]];
    out.push(map ? poly.map(([x, y]) => map(x, y)) : poly);
  }
  return out;
}

/**
 * The MODEL-frame grid lines ([{ label, start: [x, y, z], end }]) as plate geometry, for a
 * plate drawn in the model's own frame. The manifest's grid belongs to the drawings' frame
 * - gridOn is for that one - and the two are NOT interchangeable: on BA03 they are ~1,900 ft
 * apart on the plan y axis (registerFrames).
 */
export function gridFromLines(lines, bounds) {
  const out = [];
  for (const l of lines || []) {
    if (!l || !Array.isArray(l.start) || !Array.isArray(l.end)) continue;
    const s = l.start; const e = l.end;
    if (Math.abs(s[0] - e[0]) < 1e-6) {
      const v = s[0];
      if (v < bounds.xmin || v > bounds.xmax) continue;
      out.push({ axis: "x", label: l.label, from: [v, bounds.ymin], to: [v, bounds.ymax], at: v });
    } else if (Math.abs(s[2] - e[2]) < 1e-6) {
      const v = s[2];
      if (v < bounds.ymin || v > bounds.ymax) continue;
      out.push({ axis: "y", label: l.label, from: [bounds.xmin, v], to: [bounds.xmax, v], at: v });
    }
  }
  return out;
}

/** Several polygons as one SVG path on a plate. */
export const polysPathOn = (polys, project, rank) => (polys || []).map((p) => polyPath(p, project, rank)).join("");

/**
 * THE GRID LINES AND THEIR NAMES on a plate (owner 2026-11-05: "make sure the grid lines
 * and names are visible in all isometric views"). The manifest's own grid is used, which is
 * the drawings' frame - the same frame the plates are drawn in - so a line lands where the
 * drawing puts it. `x` lines run across the plan depth; `y` lines run along the width.
 * Returns the geometry only, so the browser and the server draw the same thing.
 */
export function gridOn(grid, bounds) {
  const out = [];
  for (const [label, at] of Object.entries((grid && grid.x) || {})) {
    const v = Number(at);
    if (!Number.isFinite(v) || v < bounds.xmin || v > bounds.xmax) continue;
    out.push({ axis: "x", label, from: [v, bounds.ymin], to: [v, bounds.ymax], at: v });
  }
  for (const [label, at] of Object.entries((grid && grid.y) || {})) {
    const v = Number(at);
    if (!Number.isFinite(v) || v < bounds.ymin || v > bounds.ymax) continue;
    out.push({ axis: "y", label, from: [bounds.xmin, v], to: [bounds.xmax, v], at: v });
  }
  return out;
}
